import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { VerificationCodeInput } from './VerificationCodeInput'
import { colors, font, radius, spacing } from '@mobi/component-library/Theme/Common'
import { calculateTimeLeft, formatTime } from '../utils'
interface VerificationCodeFormProps {
  length?: number
  onVerify: (code: string) => Promise<void>
  onResend: () => Promise<void>
  onEditContact?: (error: string, expiryTime: number) => void
  initialError?: string | null
  initialExpiryTime?: number
  onErrorReset?: () => void
  expiryMessage?: React.ReactNode
  errorMessage?: string
}

export const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  length = 6,
  onVerify,
  onResend,
  onEditContact,
  initialError,
  initialExpiryTime,
  onErrorReset,
  expiryMessage,
  errorMessage,
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''))
  const [error, setError] = useState(initialError ?? '')
  const [isVerifyLoading, setIsVerifyLoading] = useState(false)
  const [isResendLoading, setIsResendLoading] = useState(false)

  const [expiryTime, setExpiryTime] = useState(initialExpiryTime ?? Date.now() + 3 * 60 * 1000)
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expiryTime))

  const isExpired = timeLeft <= 0
  const isError = !!error || !!errorMessage

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expiryTime)

      if (newTimeLeft <= 0) {
        clearInterval(timer)
      }
      setTimeLeft(newTimeLeft)
    }, 1000)

    return () => clearInterval(timer)
  }, [expiryTime])

  const resetCode = () => {
    setCode(Array(length).fill(''))
  }

  const handleCodeChange = (newCode: string[]) => {
    setCode(newCode)
    if (error && setError) setError('')
    if (onErrorReset) {
      onErrorReset()
    }
  }

  const handleVerifyClick = async () => {
    const filledDigits = code.filter(digit => digit !== '').length

    if (filledDigits < 6) {
      setError('Please enter a valid verification code')
      return
    }

    setIsVerifyLoading(true)
    try {
      await onVerify(code.join(''))
      resetCode()
    } catch {
      setError('Sorry the code you have provided is incorrect')
    } finally {
      setIsVerifyLoading(false)
    }
  }

  const handleResendClick = async () => {
    setIsResendLoading(true)
    try {
      await onResend()
      resetCode()
      setError('')
      if (onErrorReset) {
        onErrorReset()
      }
      const newExpiryTime = Date.now() + 3 * 60 * 1000
      setExpiryTime(newExpiryTime)
    } catch {
      setError('Sorry, there was an error resending the code')
    } finally {
      setIsResendLoading(false)
    }
  }

  const isVerifyDisabled = code.filter(x => !isNaN(parseInt(x))).length < 6 || !!error

  return (
    <MfaFormContainer data-tid-mfa-code-verification-form>
      <VerificationCodeInput code={code} onChange={handleCodeChange} isValid={!isError} />

      {isError ? (
        <MfaFormMessageContainer>
          <Icon name='LineAlertCircle' size='1.4rem' color={colors.error[600]} />
          <MfaFormErrorMessage>{error || errorMessage}</MfaFormErrorMessage>
        </MfaFormMessageContainer>
      ) : null}

      {isExpired ? (
        (expiryMessage ?? (
          <MfaCodeFormText>Your code has expired. Please request a new one.</MfaCodeFormText>
        ))
      ) : (
        <MfaCodeFormText>
          Your code will expire in <b>{formatTime(timeLeft)}</b> mins.
        </MfaCodeFormText>
      )}

      {isExpired || !!isError ? (
        <>
          <MfaPrimaryButton
            onClick={handleResendClick}
            color='primary'
            isLoading={isVerifyLoading || isResendLoading}
          >
            Request new code
          </MfaPrimaryButton>
          {onEditContact ? (
            <MfaSecondaryButton
              onClick={() => onEditContact(error, expiryTime)}
              color='secondary_color'
            >
              <MfaButtonPrimarySpan style={{ ...font.size.lg }}>
                Edit contact details
              </MfaButtonPrimarySpan>
            </MfaSecondaryButton>
          ) : null}
        </>
      ) : (
        <>
          <MfaPrimaryButton
            onClick={handleVerifyClick}
            color='primary'
            disabled={isVerifyDisabled}
            isLoading={isVerifyLoading || isResendLoading}
          >
            Verify
          </MfaPrimaryButton>
          <MfaSecondaryButton
            onClick={handleResendClick}
            disabled={isResendLoading}
            color='secondary_color'
          >
            <MfaButtonPrimarySpan style={{ ...font.size.sm }}>
              Didn't receive a code?
            </MfaButtonPrimarySpan>
            <MfaButtonSecondarySpan> Resend</MfaButtonSecondarySpan>
          </MfaSecondaryButton>
        </>
      )}
    </MfaFormContainer>
  )
}

// ======
// Styles
// ======

const MfaFormContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '16px',
})

const MfaCodeFormText = styled.p({
  textAlign: 'left',
})

const MfaFormMessageContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minHeight: '21px',
})

const MfaFormErrorMessage = styled.p({
  color: colors.error[600],
  ...font.size.md,
  margin: '0',
})

const MfaPrimaryButton = styled(Button)({})

const MfaSecondaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  gap: '0.75rem',
  alignItems: 'center',
  fontFamily: 'inherit',
  border: 'none',
  backgroundColor: colors.neutral[100],
  padding: `${spacing.smx1} ${spacing.sm}`,
  borderRadius: radius.sm,
})

const MfaButtonPrimarySpan = styled.span({
  fontWeight: font.weight.regular,
  color: colors.neutral[800],
})

const MfaButtonSecondarySpan = styled.span({
  fontWeight: font.weight.semibold,
  fontSize: '14px',
  color: colors.studio[500],
  padding: `${spacing.smx1} 0`,
})
